import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Star, MessageSquare, Flag, Clock, ExternalLink, Printer, Copy } from "lucide-react";

const Stars = ({ value }) => (
  <span className="inline-flex items-center gap-0.5">
    {[1, 2, 3, 4, 5].map((i) => (
      <Star key={i} size={14} className={i <= Math.round(value) ? "text-amber-500 fill-amber-500" : "text-slate-300"} />
    ))}
  </span>
);

export default function ProReviews() {
  const [data, setData] = useState(null);
  const [biz, setBiz] = useState(null);
  const [replyFor, setReplyFor] = useState(null);
  const [replyText, setReplyText] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/pro/reviews").then((r) => setData(r.data)).catch(() => setData({ items: [], summary: { average: 0, count: 0 } }));
  useEffect(() => {
    load();
    api.get("/pro/business").then((r) => setBiz(r.data?.business || null)).catch(() => setBiz(null));
  }, []);

  const BACKEND = process.env.REACT_APP_BACKEND_URL;
  const googleUrl = (biz?.google_review_url || "").trim();
  const copyGoogle = async () => {
    try { await navigator.clipboard.writeText(googleUrl); toast.success("Lien copié !"); }
    catch { toast.message("Copie manuelle requise."); }
  };

  const sendReply = async (rid) => {
    if (!replyText.trim()) { toast.error("Écrivez votre réponse."); return; }
    setBusy(true);
    try {
      await api.post(`/pro/reviews/${rid}/reply`, { reply: replyText });
      toast.success("Réponse publiée.");
      setReplyFor(null); setReplyText(""); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur."); }
    finally { setBusy(false); }
  };

  const report = async (rid) => {
    const reason = window.prompt("Motif du signalement (visible par l'équipe de modération) :");
    if (!reason || reason.trim().length < 3) return;
    try {
      await api.post(`/pro/reviews/${rid}/report`, { reason });
      toast.success("Avis signalé à la modération.");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur."); }
  };

  if (!data) return <div className="text-slate-500">Chargement…</div>;

  return (
    <div data-testid="pro-reviews-page">
      <h1 className="font-heading text-2xl md:text-3xl font-extrabold text-slate-900">Avis clients</h1>
      <p className="mt-1 text-sm text-slate-500">
        Seuls les bénéficiaires ayant utilisé une de vos offres peuvent vous noter. Chaque avis est validé par notre équipe.
      </p>

      <div className="mt-5 inline-flex items-center gap-3 rounded-2xl border border-slate-200 bg-white px-4 py-3" data-testid="pro-reviews-summary">
        <Stars value={data.summary.average} />
        <span className="font-heading text-2xl font-extrabold text-slate-900">{data.summary.average || "—"}</span>
        <span className="text-sm text-slate-500">{data.summary.count} avis publié{data.summary.count > 1 ? "s" : ""}</span>
      </div>

      {/* Collecte d'avis Google */}
      <div className="mt-6 rounded-2xl border border-emerald-200 bg-emerald-50/60 p-5" data-testid="pro-google-reviews">
        <div className="flex items-center gap-2 font-heading font-extrabold text-slate-900">
          <Star size={18} className="text-amber-500 fill-amber-500"/> Collecte d'avis Google
        </div>
        <p className="mt-1 text-sm text-slate-600">
          Plus d'avis Google = plus de visibilité locale et de confiance. Imprimez le présentoir QR à poser en caisse : vos clients scannent et laissent un avis en 30 secondes.
        </p>
        {googleUrl && biz?.id ? (
          <div className="mt-4 flex flex-col sm:flex-row gap-4 items-start">
            <img src={`${BACKEND}/api/businesses/${biz.id}/google-review-qr.png`} alt="QR avis Google"
                 className="w-32 h-32 rounded-xl border border-slate-200 bg-white p-2 shrink-0" data-testid="pro-google-qr"/>
            <div className="flex flex-wrap gap-2">
              <a href={googleUrl} target="_blank" rel="noreferrer">
                <Button variant="outline" data-testid="pro-google-open"><ExternalLink size={14} className="mr-1.5"/> Ouvrir le lien</Button>
              </a>
              <Button variant="outline" onClick={copyGoogle} data-testid="pro-google-copy"><Copy size={14} className="mr-1.5"/> Copier le lien</Button>
              <a href={`${BACKEND}/api/businesses/${biz.id}/google-review-poster`} target="_blank" rel="noreferrer">
                <Button className="btn-shine text-white" data-testid="pro-google-poster"><Printer size={14} className="mr-1.5"/> Présentoir imprimable</Button>
              </a>
            </div>
          </div>
        ) : (
          <div className="mt-3 text-sm text-amber-800">
            Aucun lien d'avis Google configuré. <a href="/pro/entreprise" className="font-semibold underline" data-testid="pro-google-config-link">Ajoutez-le dans « Mon entreprise »</a>.
          </div>
        )}
      </div>

      <div className="mt-6 space-y-3">
        {data.items.length === 0 ? (
          <div className="rounded-2xl border-2 border-dashed border-slate-200 bg-white p-8 text-center" data-testid="pro-reviews-empty">
            <Star size={36} className="mx-auto text-slate-300" />
            <div className="mt-2 font-heading font-extrabold text-slate-900">Aucun avis pour le moment</div>
            <p className="mt-1 text-sm text-slate-600">Vos avis apparaîtront ici dès qu'un bénéficiaire aura utilisé une offre.</p>
          </div>
        ) : data.items.map((r) => (
          <div key={r.id} className="rounded-2xl border border-slate-200 bg-white p-4" data-testid={`pro-review-${r.id}`}>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <Stars value={r.rating} />
                <span className="font-semibold text-slate-900 text-sm">{r.author_name}</span>
                {r.status === "pending" && (
                  <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5">
                    <Clock size={10} /> En modération
                  </span>
                )}
                {r.reported && (
                  <span className="text-[10px] font-bold uppercase tracking-wider text-rose-700 bg-rose-50 border border-rose-200 rounded-full px-2 py-0.5">
                    Signalé
                  </span>
                )}
              </div>
              <span className="text-[11px] text-slate-400">{(r.created_at || "").slice(0, 10)}</span>
            </div>
            {r.comment && <p className="mt-2 text-sm text-slate-700 whitespace-pre-line">{r.comment}</p>}
            {r.pro_reply && (
              <div className="mt-3 rounded-xl bg-slate-50 border border-slate-200 p-3">
                <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Votre réponse</div>
                <p className="mt-1 text-sm text-slate-700 whitespace-pre-line">{r.pro_reply}</p>
              </div>
            )}
            {replyFor === r.id ? (
              <div className="mt-3">
                <Textarea rows={3} value={replyText} onChange={(e) => setReplyText(e.target.value)}
                          placeholder="Votre réponse publique…" data-testid={`pro-reply-input-${r.id}`} />
                <div className="mt-2 flex justify-end gap-2">
                  <Button variant="outline" size="sm" onClick={() => { setReplyFor(null); setReplyText(""); }}>Annuler</Button>
                  <Button size="sm" disabled={busy} onClick={() => sendReply(r.id)} className="btn-shine text-white" data-testid={`pro-reply-send-${r.id}`}>
                    Publier la réponse
                  </Button>
                </div>
              </div>
            ) : (
              <div className="mt-3 flex gap-2">
                <Button variant="outline" size="sm" onClick={() => { setReplyFor(r.id); setReplyText(r.pro_reply || ""); }} data-testid={`pro-reply-btn-${r.id}`}>
                  <MessageSquare size={13} className="mr-1.5" /> {r.pro_reply ? "Modifier ma réponse" : "Répondre"}
                </Button>
                <Button variant="ghost" size="sm" onClick={() => report(r.id)} className="text-rose-700 hover:bg-rose-50" data-testid={`pro-report-btn-${r.id}`}>
                  <Flag size={13} className="mr-1.5" /> Signaler
                </Button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
